import React, { useState, useEffect, useMemo } from 'react'
import {connect} from 'react-redux';
import {paymentPreviewSelect} from '../../../redux/selector/paymentPreviewSelector'
import { 
    Button, 
    ErrorMark, 
    Header, 
    NavigationLinkButton 
} from '../../../components/RegularComponents'
import { fetchApplicationReferenceData, saveSegueDetails } from '../../../redux/slice/primaryAgencyApplicationSlice';
import { computeMortgageMainAgentCost, financialNumber, replaceUrlParams } from '../../../app/utils';
import { 
    APPLICATION_MODE_NEW,
    APPLICATION_STATUS_PENDING_PAYMENT,
    APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE, 
    PAYMENT_STATUS_FAILURE, 
    PAYMENT_STATUS_INPROGRESS, 
    ROUTE_PATH, 
    SEGUE_NAME
} from '../../../app/constants';
import { ERROR_STRING } from '../../../app/errors';
import { fetchApplicationWithIdAndSetStore, initiatePayment_v1 } from '../../../redux/slice/collectionApplicationSlice';
import { fetchMultipleApplicationsWithId } from '../../../redux/slice/bltHomeSlice'
import { saveBusinessLicenceApplicationData } from '../../../redux/slice/businessLicenceApplicationSlice';
import { setAdditionalApplications } from '../../../redux/slice/additionalApplicationsSlice';
import { push } from 'connected-react-router';
import { applicationTypeFromLicenceSubType } from '../../../redux/slice/applicationsUtils';
import { usePathApplication } from '../../../redux/hooks/usePathApplication';

const MainAgentPaymentPreview = (props) => {
    const { application } = usePathApplication();
    const isPrincipalBroker =  (ROUTE_PATH.PB_APP.search(application) !== -1);
    const { paymentPreview,dispatch } = props
    const { feesStructure, paymentStatus } = paymentPreview

    const { primaryApplication, agentApplications, referenceData } = paymentPreview

    const applicationType = applicationTypeFromLicenceSubType(primaryApplication?.licencingSubType);
    const applicationMode = APPLICATION_MODE_NEW

    const [fetchingFeesData, setFetchingFeesData] = useState(false)
    const [attemptedFetchingAdditionalApplications, setAttemptedFetchingAdditionalApplications] = useState(false)
    const [attemptedFetchingPrimaryApplications, setAttemptedFetchingPrimaryApplications] = useState(false)
    const [fetchingApplicationData, setFetchingApplicationData] = useState(false)

    const [helpPanelOpen, setHelpPanelOpen] = useState(false)
    const [acceptedCardHelpPanelOpen, setAcceptedCardHelpPanelOpen] = useState(false)

    /* 
     * Fetch the fees details, if not fetched already
     */
    useEffect(() => {
        if (!referenceData
            && !fetchingFeesData) {
            setFetchingFeesData(true)
            dispatch(fetchApplicationReferenceData())
                    .then(() => setFetchingFeesData(false))
        }
    },[referenceData, fetchingFeesData, dispatch])

    useEffect(() => {

        if(attemptedFetchingAdditionalApplications) {
            // console.log("agent application data already fetched")
            return
        }

        // it is possible that the primary application data is not available yet.
        // let the other useEffect take care of fetching primary agency data and then 
        // fetch the addition application data if required.
        // Both useEffect can not run in parallel, otherwise there can be a race condition 
        // which will result in additional application not having businessLicenceData at the end.
        if(primaryApplication && primaryApplication.businessLicenceData) {
            // find out which applications are in payment pending status and which does not have the 
            // application data yet.
            const needToFetchApps = agentApplications.filter(app => (app.applicationStatus === APPLICATION_STATUS_PENDING_PAYMENT && 
                !app.businessLicenceData))

            if(needToFetchApps.length > 0) {
                // console.log("had to fetch agent application data...")
                // this flag prevents repeatition on below heavy duty call.
                setAttemptedFetchingAdditionalApplications(true)

                const applicationIds = needToFetchApps.map(app => app.id)

                // Fetch app applications which are not fetched yet. This is done to check 
                // if application data is entered till appliction review page and payment not done yet. 
                // We will have to add such applications for the payment process.
                setFetchingApplicationData(true)
                dispatch(fetchMultipleApplicationsWithId(applicationIds))
                    .then((applications) => {
                        dispatch(setAdditionalApplications({
                        branchApplications : [],
                        agentApplications : applications
                        }))
                    })
                    .finally(() => setFetchingApplicationData(false))
            } else {
                if(agentApplications.length > 0) {
                    // this flag prevents repeatition on below heavy duty call.
                    setAttemptedFetchingAdditionalApplications(true)
                }
            }
        }
        
    }, [
        primaryApplication,
        agentApplications, 
        attemptedFetchingAdditionalApplications,
        setAttemptedFetchingAdditionalApplications,
        dispatch
    ])

    useEffect(() => {

        if(attemptedFetchingPrimaryApplications) {
            // console.log("primary agency application data already fetched")
            return
        }
        
        if (primaryApplication && !primaryApplication.businessLicenceData) { 
            console.log("had to fetch primary agency application data")
            setFetchingApplicationData(true)
            setAttemptedFetchingPrimaryApplications(true)

            dispatch(fetchApplicationWithIdAndSetStore(primaryApplication.id))
                .then(data => {
                    // no need to do anything
                })
                .finally(() => {
                    setFetchingApplicationData(false)
                })
        }


    }, [
        primaryApplication,
        attemptedFetchingPrimaryApplications,
        setAttemptedFetchingPrimaryApplications,
        setFetchingApplicationData,
        dispatch
    ])

    const costBreakup = useMemo(() => {
        const agencyFeesStructure = feesStructure["mortgage-main-agent"]
        
        if(!agencyFeesStructure) {
            return {}
        }

        const { brokerageFees, brokerageRetentionFees } = agencyFeesStructure
        return computeMortgageMainAgentCost(primaryApplication,
            brokerageFees, 
            brokerageRetentionFees)
    }, [primaryApplication, feesStructure])

    const totalAmountToPay = useMemo(() => {
        const { brokerageCost, brokerageRetentionCost } = costBreakup
        return financialNumber(brokerageCost + brokerageRetentionCost)
    }, [costBreakup])


    // UI Components 
    const costBreakupTable = () => {
        if(feesStructure 
            && feesStructure["mortgage-main-agent"]
            && primaryApplication) {
            let agencyFeesStructure = feesStructure["mortgage-main-agent"]
    
            return (
                <div className="row">
                    <div className="col-lg-8">
                        <table className="table mraCostBreakupTable">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th>Quantity</th>
                                    <th>Cost</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {costBreakup.brokerageCost ? <tr>
                                    <td>{`${isPrincipalBroker ? 'Principal Mortgage Broker Application' : 'Compliance Officer Application'}`}</td>
                                    <td>1</td>
                                    <td>{`$${financialNumber(agencyFeesStructure.brokerageFees)} CDN`}</td>
                                    <td>{`$${financialNumber(costBreakup.brokerageCost)} CDN`}</td>
                                </tr> : null }
                                
                                {costBreakup.brokerageRetentionCost ? <tr>
                                    <td>{`${isPrincipalBroker ? 'Principal Mortgage Broker' : 'Compliance Officer'} Retention Application`}</td>
                                    <td>1</td>
                                    <td>{`$${financialNumber(agencyFeesStructure.brokerageRetentionFees)} CDN`}</td>
                                    <td>{`$${financialNumber(costBreakup.brokerageRetentionCost)} CDN`}</td>
                                </tr> : null}

                                <tr>
                                    <th colSpan="3">
                                        Total Cost
                                    </th>
                                    <td><strong>{`$${totalAmountToPay}`}</strong>{` CDN`}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )
        }
        
        return null
    }

    const paymentStaticText = () => {

        return (<div id="mra-payment-status-text">
                
                <div className="row bottomMarginMedium">
                    <div className="col-lg-8">
                        <span style={{fontSize: "2em"}}>
                            Choosing the right payment method
                        </span>
                    </div>
                </div>

                <div className="row bottomMarginMedium">
                    <div className="col-lg-8">
                        <h2>Paying with the credit or bank card</h2>
                    </div>

                    <div className="col-lg-8 bottomMarginNormal">
                        <span><strong>You can select to pay with a credit or debit card.</strong> We accept most debit cards with a Visa or Mastercard logo, as well as all major credit cards.</span>
                    </div>

                    <div 
                        className="col-lg-8" 
                        style={{padding: "15px", borderTop: "1px solid #777",borderBottom: "1px solid #777", cursor: "pointer"}}
                        onClick={() => {
                            setAcceptedCardHelpPanelOpen(!acceptedCardHelpPanelOpen)
                        }}
                    >
                        <div style={{color: "#0058a4"}}>
                            <strong>Which cards are accepted?</strong>
                            {acceptedCardHelpPanelOpen ? 
                                <i className="fa fa-lg fa-minus pull-right" /> : <i className="fa fa-lg fa-plus pull-right" />}
                        </div>
                        {acceptedCardHelpPanelOpen && 
                        <div style={{padding: "15px 0px"}}>
                            <strong>Using a debit card:&nbsp;</strong> If your debit card has a Visa or MasterCard logo on it try entering it using the credit, debit-credit option.
                            Get information on &nbsp;
                            <a 
                                style={{textDecoration: "underline"}} 
                                href="https://www.visa.ca/en_CA/pay-with-visa/cards/debit-cards.html" 
                                target="_blank" 
                                rel="noreferrer">
                                    Visa Debit as visa.ca
                            </a>
                            &nbsp;and&nbsp;
                            <a 
                                style={{textDecoration: "underline"}} 
                                href="https://www.mastercard.ca/en-ca/personal/find-a-card/debit-cards.html" 
                                target="_blank" 
                                rel="noreferrer">
                                    Debit MasterCard at mastercard.ca
                            </a>
                            <br /><br />
                            <strong>Using a credit card:&nbsp;</strong> 
                            Visa, MasterCard, and American Express
                        </div>
                        }
                    </div>

                    <div className="col-lg-8">
                        <h2>Paying from your bank account with Interac Online &reg;</h2>
                    </div>

                    <div className="col-lg-8 bottomMarginNormal">
                        <span><strong>Selecting to pay with Interac Online &reg;</strong>
                        &nbsp; will take you to online banking website. You'll be able to review and submit your payment after you login to your online banking account.</span>
                    </div>

                    <div 
                        className="col-lg-8"
                        style={{
                            padding: "15px", 
                            borderTop: "1px solid #777",
                            borderBottom: "1px solid #777",
                            font: "1.2em",
                            color : " #e69304"
                        }}
                    >
                        <i className="fa fa-2x fa-exclamation-triangle" />
                        &nbsp; 
                        <strong>Not all banks support payments through Interac Online &reg;</strong>
                    </div>

                </div>


                <div className="row bottomMarginLarge">
                    <div 
                        className="col-lg-8" 
                        style={{
                            paddingBottom: "15px", 
                            borderBottom: "1px solid #777", 
                            cursor: "pointer"}}
                        onClick={() => {
                            setHelpPanelOpen(!helpPanelOpen)
                        }}
                    >
                        <div style={{color: "#0058a4"}}>
                            <strong>Does my bank support Interac Online&reg; ?</strong>
                            {helpPanelOpen ? <i className="fa fa-lg fa-minus pull-right" /> : <i className="fa fa-lg fa-plus pull-right" />}
                        </div>
                        {helpPanelOpen && 
                        <div style={{padding: "15px 0px"}}>
                            <strong>Interac Online &reg; is provided by Interac Inc. in partnership with participating banks.</strong>
                            <br />
                            Not all banks choose to participate in this service. Review &nbsp;
                            <a 
                                style={{textDecoration: "underline"}} 
                                href="https://www.interac.ca/en/consumers/products/interac-debit/e-commerce/#fi-list-interac-online"
                                target="_blank" 
                                rel="noreferrer">
                                the list of participating banks
                            </a> to see if your bank is one of them.
                        </div>
                        }
                    </div>
                </div>

            </div>)
    }

    // Button handlers
    const onContinue = () => {
        dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
        .then(data => {
            dispatch(initiatePayment_v1(applicationType,applicationMode)).then(() => {
                dispatch(saveSegueDetails({
                    name : SEGUE_NAME.PAYMENT,
                    applicationType,
                    applicationMode
                }))
            })
        })
    }

    const onCancel = () => {
        // go back to the welcome screen
        const nextUrl = replaceUrlParams(ROUTE_PATH.WELCOME, 
            APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE, applicationMode)
        dispatch(push(nextUrl));
    }

    if(fetchingFeesData || fetchingApplicationData) {
        return (
            <div className={"container"}>
                <div className="row topMarginLarge">
                    <div className="col-12 centered">
                        <p className="statusText">
                            ...
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container">
            <div className="row bottomMarginLarge">
                <div className="col-lg-8">
                    <Header 
                        id="mra-payment-preview-header"
                        header="Get ready to pay"
                    />
                </div>
            </div>

            <div className="row bottomMarginNormal">
                <div className="col-lg-8">
                    <span style={{fontSize: "2em"}}>
                        Amount to pay : $
                        <span style={{fontWeight: "bold"}}>{totalAmountToPay}</span>
                    </span>
                    <span style={{fontSize: "1.5em"}}>{" CDN"}</span>
                </div>
            </div>

            {costBreakupTable()}

            {paymentStaticText()}

            <div className="row">
                <div className="col-lg-2">
                    <div className={(paymentStatus === PAYMENT_STATUS_FAILURE) ? " has-error" : ""}>
                        <Button
                            id="mra-payment-preview-continue-button" 
                            onClick = {onContinue}
                            text={"Continue"} 
                            showSpinner={paymentStatus === PAYMENT_STATUS_INPROGRESS}
                            buttonType={paymentStatus === PAYMENT_STATUS_INPROGRESS ? "regular_disabled" : "regular_primary"}
                        />
                        {(paymentStatus === PAYMENT_STATUS_FAILURE) && ErrorMark()}
                    </div>
                </div>
                <div className="col-lg-4">
                    <NavigationLinkButton 
                        id="mra-payment-preview-cancel-button"
                        text="Cancel"
                        onClick={onCancel}
                    />
                </div>

                {(paymentStatus === PAYMENT_STATUS_FAILURE) && (
                    <div className="col-lg-8 ns-error-message topMarginNormal">
                        <span className="ns-visually-hidden">Error:</span>
                        {ERROR_STRING.PAYMENT_PREVIEW_PAYMENT_FAIL}
                    </div>
                )}
            </div>
        </div>
    )
}

export default connect(paymentPreviewSelect) (MainAgentPaymentPreview)